// Translate an agent-produced `import_prefill_result` payload into the
// InqaState shape used by <InqaWizard>. Mirrors `applyInqaPrefill` from the
// legacy `web/public/app.js`, including the semantic-name → numeric-value
// fallback for the gestaltungsfelder checkboxes.

import type { InqaForm, InqaState } from "./state";

type Raw = Record<string, unknown>;

function pickString(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return undefined;
}

function asObject(value: unknown): Raw | undefined {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Raw;
  }
  return undefined;
}

const GESTALTUNGSFELDER_NAME_TO_NUMBER: Record<string, number> = {
  "führung": 3,
  "fuehrung": 3,
  "zusammenarbeit": 2,
  "lernen": 1,
  "gesundheit": 4,
  "gleichstellung": 5,
  "vielfalt": 6,
};

function normalizeGestaltungsfeld(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    const n = Math.trunc(value);
    return n >= 1 && n <= 6 ? n : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      const n = Number(trimmed);
      return n >= 1 && n <= 6 ? n : null;
    }
    const key = trimmed.toLowerCase();
    return GESTALTUNGSFELDER_NAME_TO_NUMBER[key] ?? null;
  }
  return null;
}

export function applyInqaPrefill(prev: InqaState, data: Record<string, unknown>): InqaState {
  const projekt = asObject(data.projekt) ?? data;
  const meta = asObject(data.meta) ?? data;

  const next: Partial<InqaForm> = {};
  const websiteUrl = pickString(projekt.websiteUrl, projekt.website_url);
  if (websiteUrl) next.websiteUrl = websiteUrl;
  const titel = pickString(projekt.titel, projekt.projekttitel);
  if (titel) next.projekttitel = titel;
  const branche = pickString(projekt.branche);
  if (branche) next.branche = branche;
  const mitarbeitende = pickString(projekt.mitarbeitende);
  if (mitarbeitende) next.mitarbeitende = mitarbeitende;
  const personentage = pickString(projekt.personentage);
  if (personentage) next.personentage = personentage;
  const stundenProTag = pickString(projekt.stundenProTag, projekt.stunden_pro_tag);
  if (stundenProTag) next.stundenProTag = stundenProTag;
  const zeitraumStart = pickString(projekt.zeitraumStart, projekt.zeitraum_start);
  if (zeitraumStart) next.zeitraumStart = zeitraumStart;
  const zeitraumEnd = pickString(projekt.zeitraumEnd, projekt.zeitraum_end);
  if (zeitraumEnd) next.zeitraumEnd = zeitraumEnd;
  const kontaktdaten = pickString(projekt.kontaktdaten, projekt.kontaktdaten_unternehmen);
  if (kontaktdaten) next.kontaktdaten = kontaktdaten;

  const ausgangssituation = pickString(data.ausgangssituation, data.ausgangssituation_stichpunkte);
  if (ausgangssituation) next.ausgangssituation = ausgangssituation;
  const zielsetzung = pickString(data.zielsetzung, data.zielsetzung_stichpunkte);
  if (zielsetzung) next.zielsetzung = zielsetzung;
  const kerninhalte = pickString(data.kerninhalte, data.kerninhalte_stichpunkte);
  if (kerninhalte) next.kerninhalte = kerninhalte;
  const methoden = pickString(data.methoden, data.methoden_stichpunkte);
  if (methoden) next.methoden = methoden;
  const ergebnisse = pickString(data.ergebnisse, data.ergebnisse_stichpunkte);
  if (ergebnisse) next.ergebnisse = ergebnisse;

  const coachName = pickString(meta.coachName, meta.coach_name);
  if (coachName) next.coachName = coachName;
  const coachUnternehmen = pickString(meta.coachUnternehmen, meta.coach_unternehmen);
  if (coachUnternehmen) next.coachUnternehmen = coachUnternehmen;

  const felderRaw = Array.isArray(data.gestaltungsfelder) ? data.gestaltungsfelder : null;
  if (felderRaw) {
    const normalized = felderRaw
      .map(normalizeGestaltungsfeld)
      .filter((n): n is number => n !== null);
    const unique = Array.from(new Set(normalized)).sort((a, b) => a - b);
    if (unique.length > 0) next.gestaltungsfelder = unique;
  }

  const rollenRaw = Array.isArray(data.beteiligte_rollen) ? data.beteiligte_rollen : null;
  if (rollenRaw) {
    const rollen = rollenRaw
      .map((r) => (r == null ? "" : String(r).trim()))
      .filter((s) => s.length > 0);
    if (rollen.length > 0) next.rollen = rollen;
  }

  return {
    ...prev,
    phase: "wizard",
    form: { ...prev.form, ...next },
    step: 1,
  };
}
